import { useCallback, useEffect, useState } from 'react';
import {
  Abas,
  Aviso,
  Botao,
  Carregando,
  Cartao,
  Etiqueta,
  Icone,
  paginar,
  Paginacao,
  Select,
  Selo,
  useDialogo,
  Vazio,
} from '../../componentes/ui/index.ts';
import { api, dataHora, ErroApi } from '../../lib/api.ts';
import { exportarXlsx } from '../../lib/exportar.ts';
import { ROTULO_PAPEL, type Papel, type StatusUsuario, type Usuario } from '../../lib/tipos.ts';

const ABAS = [
  { valor: 'pendente' as const, rotulo: 'Aguardando' },
  { valor: 'aprovado' as const, rotulo: 'Com acesso' },
  { valor: 'rejeitado' as const, rotulo: 'Recusados' },
  { valor: 'revogado' as const, rotulo: 'Revogados' },
];

const PAPEIS = [
  { valor: 'admin' as Papel, rotulo: 'Administrador', descricao: 'Acesso a tudo' },
  {
    valor: 'financeiro' as Papel,
    rotulo: 'Financeiro',
    descricao: 'Só contato e pagamentos',
  },
];

export function Usuarios() {
  const [aba, setAba] = useState<StatusUsuario>('pendente');
  const [lista, setLista] = useState<Usuario[]>([]);
  const [pendentes, setPendentes] = useState(0);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [pagina, setPagina] = useState(1);

  // Trocar de aba volta para a primeira página.
  useEffect(() => setPagina(1), [aba]);
  const { perguntar, dialogo } = useDialogo();

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const { usuarios } = await api<{ usuarios: Usuario[] }>(`/api/usuarios?status=${aba}`);
      setLista(usuarios);

      // Contagem da fila fica visível em qualquer aba: é a ação que trava
      // outras pessoas de trabalhar.
      if (aba === 'pendente') setPendentes(usuarios.length);
      else {
        const p = await api<{ usuarios: Usuario[] }>('/api/usuarios?status=pendente');
        setPendentes(p.usuarios.length);
      }
      setErro(null);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.mensagem : 'Erro ao carregar');
    } finally {
      setCarregando(false);
    }
  }, [aba]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function agir(
    id: string,
    caminho: string,
    corpo: unknown,
    metodo: 'POST' | 'PATCH' = 'POST',
  ) {
    setOcupado(id);
    setErro(null);
    try {
      await api(`/api/usuarios/${id}/${caminho}`, { metodo, corpo });
      await carregar();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.mensagem : 'Erro na operação');
    } finally {
      setOcupado(null);
    }
  }

  const visivel = paginar(lista, pagina);

  // A planilha leva a lista inteira, não só a página que está na tela.
  function exportar() {
    exportarXlsx(
      `usuarios-${aba}`,
      lista.map((u) => ({
        Nome: u.nome,
        'E-mail': u.email,
        Papel: u.papel ? ROTULO_PAPEL[u.papel] : '',
        Situação: u.status,
        Cadastrado: dataHora(u.criadoEm),
        Aprovado: u.aprovadoEm ? dataHora(u.aprovadoEm) : '',
        'Motivo da recusa': u.motivoRejeicao ?? '',
      })),
    );
  }

  return (
    <div className="pilha-6">
      {dialogo}

      <div className="linha linha-entre">
        <div className="pilha-3">
          <Etiqueta>Permissões</Etiqueta>
          <h2>Acessos ao painel</h2>
          <p className="texto-suave" style={{ maxWidth: '60ch' }}>
            Qualquer pessoa pode criar uma conta, mas ela só funciona depois que você aprovar e
            escolher o papel.
          </p>
        </div>
        <Botao
          variante="secundario"
          tamanho="pequeno"
          disabled={lista.length === 0}
          onClick={exportar}
          icone={<Icone.Download tamanho={14} />}
        >
          Exportar planilha
        </Botao>
      </div>

      <Abas
        rotulo="Filtrar por situação"
        valor={aba}
        aoMudar={setAba}
        opcoes={ABAS.map((a) => ({
          ...a,
          contagem: a.valor === 'pendente' ? pendentes : undefined,
        }))}
      />

      {erro && <Aviso tipo="erro">{erro}</Aviso>}

      {carregando ? (
        <Carregando />
      ) : lista.length === 0 ? (
        <Vazio titulo={aba === 'pendente' ? 'Nenhum pedido pendente' : 'Nada por aqui'}>
          {aba === 'pendente'
            ? 'Quando alguém criar uma conta, o pedido aparece nesta lista.'
            : 'Nenhum usuário nesta situação.'}
        </Vazio>
      ) : (
        <div className="pilha">
          {visivel.itens.map((u) => (
            <Cartao key={u.id} marcado={u.status === 'pendente'}>
              <div className="linha linha-entre linha-topo">
                <div style={{ minWidth: 0 }}>
                  <h3 style={{ fontSize: 'var(--texto-lg)' }}>{u.nome}</h3>
                  <div className="texto-suave texto-sm">{u.email}</div>
                  <div className="texto-fraco" style={{ marginTop: 'var(--gap-2)' }}>
                    Cadastrado em {dataHora(u.criadoEm)}
                    {u.aprovadoEm && ` · aprovado em ${dataHora(u.aprovadoEm)}`}
                    {u.motivoRejeicao && ` · motivo: ${u.motivoRejeicao}`}
                  </div>
                </div>

                {u.papel && (
                  <Selo tom={u.papel === 'admin' ? 'laranja' : 'neutro'} ponto>
                    {ROTULO_PAPEL[u.papel]}
                  </Selo>
                )}
              </div>

              <div className="linha" style={{ marginTop: 'var(--gap-4)' }}>
                {u.status === 'pendente' && (
                  <>
                    {PAPEIS.map((p) => (
                      <Botao
                        key={p.valor}
                        tamanho="pequeno"
                        variante={p.valor === 'admin' ? 'primario' : 'secundario'}
                        disabled={ocupado === u.id}
                        onClick={() => void agir(u.id, 'aprovar', { papel: p.valor })}
                        icone={<Icone.Check tamanho={14} />}
                      >
                        Aprovar como {p.rotulo}
                      </Botao>
                    ))}
                    <Botao
                      variante="perigo"
                      tamanho="pequeno"
                      disabled={ocupado === u.id}
                      onClick={() =>
                        perguntar({
                          titulo: `Recusar ${u.nome}?`,
                          texto: 'A conta continua existindo, mas o login fica bloqueado.',
                          campo: { rotulo: 'Motivo', dica: 'Aparece para a pessoa no aviso.' },
                          confirmar: 'Recusar',
                          perigo: true,
                          aoConfirmar: (motivo) =>
                            agir(u.id, 'rejeitar', { motivo: motivo || undefined }),
                        })
                      }
                    >
                      Recusar
                    </Botao>
                  </>
                )}

                {u.status === 'aprovado' && (
                  <>
                    <div style={{ minWidth: '13rem' }}>
                      <Select
                        opcoes={PAPEIS}
                        valor={u.papel}
                        desabilitado={ocupado === u.id}
                        aoMudar={(papel) => void agir(u.id, 'papel', { papel }, 'PATCH')}
                      />
                    </div>

                    <Botao
                      variante="perigo"
                      tamanho="pequeno"
                      disabled={ocupado === u.id}
                      onClick={() =>
                        perguntar({
                          titulo: `Revogar o acesso de ${u.nome}?`,
                          texto:
                            'As sessões abertas são encerradas na hora. A conta e o histórico de ações continuam preservados.',
                          confirmar: 'Revogar acesso',
                          perigo: true,
                          aoConfirmar: () => agir(u.id, 'revogar', {}),
                        })
                      }
                    >
                      Revogar acesso
                    </Botao>
                  </>
                )}

                {(u.status === 'rejeitado' || u.status === 'revogado') &&
                  PAPEIS.map((p) => (
                    <Botao
                      key={p.valor}
                      variante="vazado"
                      tamanho="pequeno"
                      disabled={ocupado === u.id}
                      onClick={() => void agir(u.id, 'aprovar', { papel: p.valor })}
                    >
                      Liberar como {p.rotulo}
                    </Botao>
                  ))}
              </div>
            </Cartao>
          ))}
        </div>
      )}

      <Paginacao total={lista.length} pagina={visivel.pagina} aoMudar={setPagina} />
    </div>
  );
}
